import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Tokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface SessionUser {
  id: string;
  tenantId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'MANAGER' | 'REVIEWER' | 'OPERATOR' | 'VIEWER';
  signatureAgreed: boolean;
  isSuperAdmin?: boolean;
  mfaEnabled?: boolean;
  emailVerifiedAt?: string | null;
}

export interface SessionTenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  gxpValidated: boolean;
  brandingColor?: string | null;
  brandingLogo?: string | null;
  billingRegion?: string | null;
  billingCurrency?: string | null;
  onboardingCompletedAt?: string | null;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  tenant: SessionTenant | null;
  setSession(data: { user: SessionUser; tenant: SessionTenant; tokens: Tokens }): void;
  setTokens(tokens: Tokens): void;
  clear(): void;
  isAuthenticated(): boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      tenant: null,
      setSession: ({ user, tenant, tokens }) =>
        set({
          user,
          tenant,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        }),
      setTokens: (tokens) =>
        set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null, tenant: null }),
      isAuthenticated: () => !!get().accessToken && !!get().user,
    }),
    {
      name: 'praxara.auth',
    },
  ),
);
